import React, { useState, useRef, useEffect, useCallback } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { saveAs } from 'file-saver';
import axiosInstance from '../utils/axiosInstance';
import { renderMarkdown, getMarkdownStyles } from '../utils/markdownRenderer';
import { KwandikaRaporoCard, OngerahoUmukonoCard, IzinaRyaDosiyeCard } from '../components/MobileUICards';

/* ─── Recipient → statut mapping ─── */
const recipientToStatut = (r) => {
  if (r === 'Umudugudu') return 'Itegereje Umudugudu';
  if (r === 'Akagari')   return 'Itegereje Akagari';
  return 'Inyandiko itaruzura';
};

/* ─── Strip HTML tags for content validation ─── */
const stripHtml = (html) => html.replace(/<[^>]*>/g, '').trim();

/* ─── Export helpers ─── */
const exportPDF = async ({ title, author, department, recipient, date, content, signature, signerName, signedDate }, fn) => {
  const renderedHtml = renderMarkdown(content || '');
  const sigBlock = signature
    ? `<div style="margin-top:20px;padding-top:15px;border-top:2px solid #2563eb;">
        <p style="font-size:11px;font-weight:700;color:#4b5563;font-family:sans-serif;">Umukono:</p>
        <img src="${signature}" style="max-height:50px;max-width:180px;display:block;margin:8px 0;" />
        <p style="font-size:13px;font-weight:700;color:#1e293b;font-family:sans-serif;margin:4px 0 2px;">${signerName || ''}</p>
        <p style="font-size:10px;color:#6b7280;font-family:sans-serif;margin:0;">${signedDate || ''}</p>
      </div>`
    : '';
  const htmlContent = `
    <div style="font-family:Georgia,'Times New Roman',serif;max-width:700px;margin:0 auto;padding:20px;color:#1e293b;">
      <style>${getMarkdownStyles()}</style>
      <div style="text-align:center;border-bottom:3px solid #2563eb;padding-bottom:15px;margin-bottom:20px;">
        <h1 style="font-size:20px;font-weight:800;color:#1d4ed8;margin:0;">${title || 'Raporo'}</h1>
        <p style="font-size:11px;color:#6b7280;margin:6px 0 0;">
          ${recipient ? `<strong>Uwo bireba:</strong> ${recipient} &nbsp;|&nbsp; ` : ''}
          ${author || ''} ${department ? `| ${department}` : ''} ${date ? `| ${date}` : ''}
        </p>
      </div>
      ${renderedHtml}
      ${sigBlock}
      <div style="margin-top:30px;border-top:1px solid #e2e8f0;padding-top:10px;font-size:10px;color:#94a3b8;text-align:center;">
        Raporo yateguwe na sisitemu — ${signedDate || date || ''}
      </div>
    </div>`;

  const container = document.createElement('div');
  container.style.cssText = 'position:fixed;top:0;left:0;width:700px;background:#fff;z-index:-9999;opacity:0;pointer-events:none;';
  container.innerHTML = htmlContent;
  document.body.appendChild(container);

  try {
    const canvas = await html2canvas(container, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff',
      logging: false,
      width: container.scrollWidth,
      height: container.scrollHeight,
    });
    const imgData = canvas.toDataURL('image/jpeg', 0.95);
    const imgWidth = 190;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    const pdf = new jsPDF('p', 'mm', 'a4');
    let heightLeft = imgHeight;
    let position = 0;
    const pageHeight = 297;
    const margin = 10;
    pdf.addImage(imgData, 'JPEG', margin, position + margin, imgWidth, imgHeight);
    heightLeft -= pageHeight - 2 * margin;
    while (heightLeft > 0) {
      position = heightLeft - imgHeight + (pageHeight - 2 * margin);
      pdf.addPage();
      pdf.addImage(imgData, 'JPEG', margin, position + margin, imgWidth, imgHeight);
      heightLeft -= pageHeight - 2 * margin;
    }
    pdf.save(`${fn}.pdf`);
  } finally {
    document.body.removeChild(container);
  }
};

const exportWord = ({ title, author, department, recipient, date, content, signature, signerName, signedDate }, fn) => {
  const sanitize = (t) => (t || '').replace(/\u00a0/g, ' ').replace(/&nbsp;/g, ' ');
  const renderedHtml = renderMarkdown(content || '');
  const sigBlock = signature
    ? `<div class="sig"><p><b>Umukono:</b></p><img src="${signature}" width="180" style="margin:8px 0"/><p><b>${sanitize(signerName)}</b></p><p style="color:#999;font-size:9pt">${sanitize(signedDate)}</p></div>`
    : '';
  const html = `<html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
<head><meta charset='utf-8'><title>${sanitize(title)}</title>
<style>
body{font-family:Calibri,sans-serif;margin:40px;color:#333}
h1{color:#2563EB;font-size:22pt;margin-bottom:8px}
.recipient{color:#1d4ed8;font-size:11pt;font-weight:bold;margin-bottom:6px}
.meta{color:#888;font-size:10pt;margin-bottom:16px}
.content{font-size:11pt;line-height:1.8;margin-bottom:30px}
.sig{border-top:2px solid #2563EB;padding-top:16px;margin-top:30px}
${getMarkdownStyles()}
.md-renderer-body{font-size:11pt;line-height:1.8;}
</style>
</head><body>
<h1>${sanitize(title)}</h1>
${recipient ? `<div class="recipient">${sanitize(recipient)}</div>` : ''}
<div class="meta"><b>Uwakoze:</b> ${sanitize(author)} &nbsp;|&nbsp; <b>Ishami:</b> ${sanitize(department)} &nbsp;|&nbsp; <b>Itariki:</b> ${sanitize(date)}</div>
<hr style="border:1px solid #2563EB;margin-bottom:20px"/>
<div class="content">${renderedHtml}</div>
${sigBlock}
</body></html>`;
  saveAs(new Blob(['\ufeff', html], { type: 'application/msword' }), `${fn}.doc`);
};

/* ─── Templates ─── */
const TEMPLATES = [
  {
    id: 'umudugudu',
    icon: '📄',
    name: "Raporo y'Umudugudu",
    desc: 'Raporo rusange y\'ibikorwa by\'umudugudu',
    color: 'from-blue-50 to-blue-100',
    accent: '#2563eb',
    title: "Raporo y'Ibikorwa by'Umudugudu",
    department: 'Umudugudu',
    content: `INTANGIRIRO
Raporo iyi igamije gutanga amakuru y'ibikorwa byakozwe mu mudugudu mu gihe cy'amezi atatu ashize.

IBIKORWA BYAKOZWE
1. Gutunga amahoro no umutekano mu mudugudu
2. Gufasha abaturage bafite ibibazo by'ubuzima
3. Guteza imbere ubukungu bw'abaturage
4. Gukurikirana imibereho y'abaturage

IBYABONETSE
- Abaturage: [Injiza umubare]
- Ingo: [Injiza umubare]
- Ibibazo byagaragaye: [Sobanura]

IBYASABWA
1. Gukomeza gufasha abaturage bafite ubukene
2. Kongera ibikorwa by'iterambere
3. Gushyira mu bikorwa gahunda nshya

IGICUMBI
Raporo iyi igaragaza ko ibikorwa byagenze neza. Tuzakomeza gukora neza kugira ngo abaturage babeho neza.
`,
  },
  {
    id: 'ubuzima',
    icon: '🏥',
    name: "Raporo y'Ubuzima",
    desc: 'Raporo y\'imibereho y\'ubuzima bw\'abaturage',
    color: 'from-green-50 to-green-100',
    accent: '#16a34a',
    title: "Raporo y'Ubuzima bw'Abaturage",
    department: 'Ubuzima',
    content: `INTANGIRIRO
Raporo iyi igamije gutanga amakuru y'imibereho y'ubuzima bw'abaturage mu gihe gishize.

IMIBEREHO Y'UBUZIMA
1. Umubare w'abarwayi: [Injiza umubare]
2. Indwara zikunda kugaragara: [Sobanura]
3. Abana bafite imirire mibi: [Injiza umubare]
4. Abagore batwite: [Injiza umubare]

IBYABONETSE
- Ubuzima bw'abaturage bugenda neza
- Ibibazo by'imirire biracyahari
- Abaturage benshi bagera kwa muganga

IBYASABWA
1. Kongera gukurikirana abana bafite imirire mibi
2. Gushyira imbere gahunda y'ubuzima bw'abagore batwite
3. Gukomeza inyigisho z'ubuzima

IGICUMBI
Ubuzima bw'abaturage bugenda neza ariko hakenewe gukomeza gukurikirana ibibazo byagaragaye.
`,
  },
  {
    id: 'ishuri',
    icon: '🎓',
    name: "Raporo y'Ishuri",
    desc: 'Raporo y\'ibikorwa by\'uburezi',
    color: 'from-yellow-50 to-yellow-100',
    accent: '#d97706',
    title: "Raporo y'Ibikorwa by'Uburezi",
    department: 'Uburezi',
    content: `INTANGIRIRO
Raporo iyi igamije gutanga amakuru y'ibikorwa by'uburezi byakozwe mu gihe cy'igihembwe.

IBIKORWA BY'UBUREZI
1. Umubare w'abanyeshuri: [Injiza umubare]
2. Abanyeshuri bize neza: [Injiza umubare]
3. Abanyeshuri basize ishuri: [Injiza umubare]
4. Inyigisho zahawe: [Sobanura]

IBYABONETSE
- Uburezi bugenda neza mu mashuri
- Abanyeshuri benshi baza ku ishuri buri munsi
- Ibibazo by'ibikoresho biracyahari

IBYASABWA
1. Kongera ibikoresho by'ishuri
2. Gufasha abanyeshuri bafite ibibazo
3. Gukomeza gukurikirana abanyeshuri

IGICUMBI
Uburezi bugenda neza. Tuzakomeza gukora neza kugira ngo abanyeshuri bose bige neza.
`,
  },
  {
    id: 'ubukungu',
    icon: '💼',
    name: "Raporo y'Ubukungu",
    desc: 'Raporo y\'imibereho y\'ubukungu',
    color: 'from-purple-50 to-purple-100',
    accent: '#7c3aed',
    title: "Raporo y'Imibereho y'Ubukungu",
    department: 'Ubukungu',
    content: `INTANGIRIRO
Raporo iyi igamije gutanga amakuru y'imibereho y'ubukungu bw'abaturage mu gihe gishize.

IMIBEREHO Y'UBUKUNGU
1. Umubare w'ingo zifite akazi: [Injiza umubare]
2. Umubare w'ingo zidafite akazi: [Injiza umubare]
3. Ibikorwa by'ubukungu: [Sobanura]
4. Umusanzu w'abaturage: [Injiza umubare]

IBYABONETSE
- Ubukungu bw'abaturage bugenda neza
- Ingo nyinshi zifite akazi
- Ibibazo by'ubukene biracyahari

IBYASABWA
1. Gufasha ingo zidafite akazi
2. Kongera ibikorwa by'ubukungu
3. Gushyira imbere gahunda y'iterambere

IGICUMBI
Ubukungu bw'abaturage bugenda neza ariko hakenewe gukomeza gufasha ingo zifite ubukene.
`,
  },
  {
    id: 'umutekano',
    icon: '🛡️',
    name: "Raporo y'Umutekano",
    desc: 'Raporo y\'imibereho y\'umutekano',
    color: 'from-red-50 to-red-100',
    accent: '#dc2626',
    title: "Raporo y'Imibereho y'Umutekano",
    department: 'Umutekano',
    content: `INTANGIRIRO
Raporo iyi igamije gutanga amakuru y'imibereho y'umutekano mu mudugudu mu gihe gishize.

IMIBEREHO Y'UMUTEKANO
1. Ibikorwa by'umutekano byakozwe: [Sobanura]
2. Ibibazo by'umutekano byagaragaye: [Sobanura]
3. Umubare w'inzitizi: [Injiza umubare]
4. Ibikorwa byo gukumira: [Sobanura]

IBYABONETSE
- Umutekano mu mudugudu ugenda neza
- Ibibazo bike byagaragaye
- Abaturage batuye amahoro

IBYASABWA
1. Gukomeza gukurikirana umutekano
2. Kongera ibikorwa byo gukumira ibibazo
3. Gufasha abaturage kumenya amategeko

IGICUMBI
Umutekano mu mudugudu ugenda neza. Tuzakomeza gukora neza kugira ngo abaturage batuye amahoro.
`,
  },
];

/* ─── Template Modal ─── */
const TemplateModal = ({ onClose, onSelect }) => {
  const [preview, setPreview] = useState(null);
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(6px)' }}
      onClick={onClose}>
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 flex-shrink-0"
          style={{ background: 'linear-gradient(135deg, #1d4ed8, #0ea5e9)' }}>
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center text-lg">📚</span>
            <div>
              <h2 className="font-bold text-white text-base">Inyandikorugero</h2>
              <p className="text-blue-100 text-xs">Hitamo inyandikorugero ikugufikira</p>
            </div>
          </div>
          <button onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-xl bg-white/20 text-white hover:bg-white/30 transition-colors font-bold">✕</button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Template grid */}
          <div className="flex-1 overflow-y-auto p-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {TEMPLATES.map(t => (
                <div key={t.id}
                  onClick={() => setPreview(t)}
                  className={`relative cursor-pointer rounded-2xl border-2 p-4 transition-all hover:-translate-y-0.5 hover:shadow-md bg-gradient-to-br ${
                    preview?.id === t.id ? 'border-blue-500 shadow-md' : 'border-transparent hover:border-blue-200'
                  } ${t.color}`}>
                  {preview?.id === t.id && (
                    <span className="absolute top-3 right-3 w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center text-white text-xs">✓</span>
                  )}
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">{t.icon}</span>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-sm text-gray-800">{t.name}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{t.desc}</p>
                      <p className="text-xs mt-2 text-gray-400 line-clamp-2 leading-relaxed">
                        {t.content.slice(0, 80)}...
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Preview pane */}
          {preview && (
            <div className="w-64 border-l border-gray-100 flex flex-col flex-shrink-0">
              <div className="p-4 border-b border-gray-100">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">Reba mbere</p>
                <div className="flex items-center gap-2">
                  <span className="text-xl">{preview.icon}</span>
                  <span className="font-bold text-sm text-gray-800">{preview.name}</span>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                <pre className="text-xs text-gray-500 whitespace-pre-wrap leading-relaxed font-sans">{preview.content}</pre>
              </div>
              <div className="p-4 border-t border-gray-100">
                <button onClick={() => { onSelect(preview); onClose(); }}
                  className="w-full py-2.5 text-sm font-bold text-white rounded-xl transition-all hover:-translate-y-0.5 hover:shadow-lg"
                  style={{ background: `linear-gradient(135deg, ${preview.accent}, #0ea5e9)` }}>
                  Koresha iyi nyandikorugero →
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between flex-shrink-0">
          <p className="text-xs text-gray-400">{TEMPLATES.length} inyandikorugero zihari</p>
          <button onClick={onClose}
            className="px-4 py-2 text-sm font-bold border-2 border-gray-200 text-gray-600 rounded-xl hover:bg-gray-50 transition-colors">Reka</button>
        </div>
      </div>
    </div>
  );
};

/* ─── Signature Modal ─── */
const PEN_COLORS = [
  { color: '#1e293b', name: 'Umukara' },
  { color: '#1e3a8a', name: 'Ubururu' },
  { color: '#dc2626', name: 'Umutuku' },
  { color: '#047857', name: 'Icyatsi' },
  { color: '#7c3aed', name: 'Umuragara' },
];

const SignatureModal = ({ onClose, onSave }) => {
  const sigRef = useRef(null);
  const imgRef = useRef(null);
  const [tab, setTab] = useState('draw');
  const [style, setStyle] = useState('ink');
  const [color, setColor] = useState('#1e293b');
  const [width, setWidth] = useState(2);
  const [uploadedImg, setUploadedImg] = useState(null);

  const STYLES = { ink: { color: '#1e293b', width: 2 }, marker: { color: '#dc2626', width: 4 }, pencil: { color: '#4b5563', width: 1.5 } };

  const applyStyle = (s) => { setStyle(s); setColor(STYLES[s].color); setWidth(STYLES[s].width); };

  const handleCapture = () => {
    if (tab === 'image' && uploadedImg) { onSave(uploadedImg); return; }
    if (sigRef.current && !sigRef.current.isEmpty()) {
      onSave(sigRef.current.toDataURL('image/png'));
    }
  };

  const handleImg = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setUploadedImg(ev.target.result);
    reader.readAsDataURL(file);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{ background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(4px)' }} onClick={onClose}>
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden" onClick={e => e.stopPropagation()}>

        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4" style={{ background: 'linear-gradient(135deg, #1d4ed8, #0ea5e9)' }}>
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center text-lg">✍️</span>
            <h2 className="font-bold text-white text-base tracking-tight">Ongeraho Umukono</h2>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-xl bg-white/20 text-white hover:bg-white/30 transition-colors text-sm font-bold">✕</button>
        </div>

        <div className="p-6">
          {/* Tabs */}
          <div className="flex gap-2 mb-5 p-1 bg-gray-100 rounded-2xl">
            {[['draw', '✍️ Vuga'], ['image', '📷 Shyiraho']].map(([k, label]) => (
              <button key={k} onClick={() => setTab(k)}
                className={`flex-1 py-2 text-sm font-bold rounded-xl transition-all ${
                  tab === k ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}>
                {label}
              </button>
            ))}
          </div>

          {tab === 'draw' ? (
            <>
              {/* Style buttons */}
              <div className="flex gap-2 mb-4">
                {[['ink', '🖊️', 'Ikaramu'], ['marker', '🖍️', 'Marker'], ['pencil', '✏️', 'Penseri']].map(([k, icon, lbl]) => (
                  <button key={k} onClick={() => applyStyle(k)}
                    className={`flex-1 flex flex-col items-center gap-1 py-2.5 rounded-2xl border-2 text-xs font-bold transition-all ${
                      style === k
                        ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-sm'
                        : 'border-gray-100 bg-gray-50 text-gray-500 hover:border-blue-200 hover:bg-blue-50/50'
                    }`}>
                    <span className="text-base">{icon}</span>
                    {lbl}
                  </button>
                ))}
              </div>

              {/* Color row */}
              <div className="flex items-center gap-3 mb-4 px-1">
                <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">Ibara</span>
                <div className="flex gap-2 flex-1">
                  {PEN_COLORS.map(({ color: c, name }) => (
                    <button key={c} title={name} onClick={() => setColor(c)}
                      className={`w-8 h-8 rounded-full transition-all hover:scale-110 ${
                        color === c ? 'ring-2 ring-offset-2 ring-blue-500 scale-110 shadow-md' : 'ring-1 ring-gray-200'
                      }`}
                      style={{ backgroundColor: c }} />
                  ))}
                </div>
              </div>

              {/* Width row */}
              <div className="flex items-center gap-3 mb-4 px-1">
                <span className="text-xs font-bold text-gray-400 uppercase tracking-widest whitespace-nowrap">Ubugari</span>
                <input type="range" min="0.5" max="5" step="0.5" value={width} onChange={e => setWidth(parseFloat(e.target.value))}
                  className="flex-1 h-1.5 accent-blue-600 cursor-pointer" />
                <span className="text-xs font-black text-blue-600 bg-blue-50 px-2 py-1 rounded-lg min-w-[40px] text-center">{width}px</span>
              </div>

              {/* Canvas */}
              <div className="relative rounded-2xl overflow-hidden border-2 border-dashed border-blue-200 bg-gradient-to-br from-slate-50 to-blue-50 hover:border-blue-400 transition-colors">
                <SignatureCanvas ref={sigRef} penColor={color} minWidth={width * 0.75} maxWidth={width * 1.5}
                  canvasProps={{ width: 460, height: 150, className: 'block cursor-crosshair w-full touch-none rounded-2xl' }} />
                <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-blue-200 text-sm pointer-events-none font-bold select-none">
                  ✍️ Vuga hano
                </span>
              </div>

              {/* Undo / Clear / Confirm */}
              <div className="flex gap-2 mt-3">
                <button onClick={() => { const d = sigRef.current?.toData(); if (d?.length) { d.pop(); sigRef.current.fromData(d); } }}
                  className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold border-2 border-gray-200 text-gray-600 rounded-xl hover:bg-gray-50 transition-colors">
                  ↩ Subira
                </button>
                <button onClick={() => sigRef.current?.clear()}
                  className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold border-2 border-red-100 text-red-500 rounded-xl hover:bg-red-50 transition-colors">
                  ✕ Siba
                </button>
                <button onClick={handleCapture}
                  className="flex-1 py-2 text-sm font-bold text-white rounded-xl transition-all hover:-translate-y-0.5 hover:shadow-lg active:translate-y-0"
                  style={{ background: 'linear-gradient(135deg, #1d4ed8, #0ea5e9)' }}>
                  ✓ Emeza Umukono
                </button>
              </div>
            </>
          ) : (
            <div className="rounded-2xl border-2 border-dashed border-blue-200 bg-gradient-to-br from-slate-50 to-blue-50 p-8 text-center">
              {uploadedImg ? (
                <div className="flex flex-col items-center gap-3">
                  <img src={uploadedImg} alt="Umukono" className="max-h-28 object-contain rounded-xl shadow" />
                  <button onClick={() => setUploadedImg(null)} className="text-xs font-bold text-red-500 hover:underline">✕ Siba</button>
                </div>
              ) : (
                <label className="cursor-pointer flex flex-col items-center gap-3 text-gray-400">
                  <span className="w-14 h-14 bg-blue-100 rounded-2xl flex items-center justify-center text-3xl">📷</span>
                  <span className="text-sm font-semibold text-gray-600">Kanda hano gushyiraho ifoto y'umukono</span>
                  <span className="text-xs bg-gray-100 px-3 py-1 rounded-full">PNG · JPG</span>
                  <input ref={imgRef} type="file" accept="image/*" onChange={handleImg} className="hidden" />
                </label>
              )}
              <button onClick={handleCapture} disabled={!uploadedImg}
                className="mt-4 w-full py-2.5 text-sm font-bold text-white rounded-xl transition-all hover:-translate-y-0.5 hover:shadow-lg active:translate-y-0 disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ background: 'linear-gradient(135deg, #1d4ed8, #0ea5e9)' }}>
                ✓ Emeza Umukono
              </button>
            </div>
          )}

          {/* Footer */}
          <div className="flex justify-end mt-4">
            <button onClick={onClose} className="px-5 py-2.5 text-sm font-bold border-2 border-gray-200 text-gray-600 rounded-xl hover:bg-gray-50 transition-colors">Reka</button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─── Summary Panel ─── */
const SummaryPanel = ({ title, author, recipient, wordCount, charCount, lineCount }) => {
  const stats = [
    { label: 'Amagambo', value: wordCount, color: 'bg-blue-50 text-blue-700' },
    { label: 'Inyuguti', value: charCount, color: 'bg-purple-50 text-purple-700' },
    { label: 'Imirongo', value: lineCount, color: 'bg-green-50 text-green-700' },
  ];
  return (
    <div className="bg-white rounded-2xl shadow-md border border-blue-50 p-5">
      <div className="flex items-center gap-2 mb-4">
        <span className="w-1 h-5 rounded-full bg-purple-500 inline-block"></span>
        <h3 className="font-bold text-sm text-gray-700 uppercase tracking-wider">Ibyabonetse muri Raporo</h3>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-3">
        {stats.map(({ label, value, color }) => (
          <div key={label} className={`flex flex-col items-center py-3 px-1 rounded-xl ${color}`}>
            <span className="text-lg font-black">{value}</span>
            <span className="text-[10px] font-semibold mt-0.5 opacity-80 whitespace-nowrap">{label}</span>
          </div>
        ))}
      </div>
      <div className="flex flex-col gap-2">
        {[['Umutwe', title], ['Uwakoze', author], ['Uwo bireba', recipient]].map(([label, value]) => (
          <div key={label} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-xl">
            <span className="text-xs font-bold text-gray-400 uppercase tracking-wide">{label}</span>
            <span className={`text-xs font-semibold truncate max-w-[55%] text-right ${
              label === 'Uwo bireba' && value ? 'text-blue-700 font-bold' : 'text-gray-700'
            }`}>{value || '—'}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ─── Main Component ─── */
const ReportPage = () => {
  // ── Detect role from JWT ──
  const userRole = (() => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return null;
      return JSON.parse(atob(token.split('.')[1])).role || null;
    } catch { return null; }
  })();

  const defaultRecipient = userRole === 'Isibo' ? 'Umudugudu'
    : userRole === 'Umudugudu' ? 'Akagari' : '';

  // Auto-fill author from JWT fullname
  const jwtFullname = (() => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return '';
      const p = JSON.parse(atob(token.split('.')[1]));
      return p.fullname || p.name || '';
    } catch { return ''; }
  })();

  const [title, setTitle] = useState('');
  const [author, setAuthor] = useState(jwtFullname);
  const [department, setDepartment] = useState('');
  const [recipient, setRecipient] = useState(defaultRecipient);
  const [recipientCustom, setRecipientCustom] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [content, setContent] = useState('');
  const [sigData, setSigData] = useState(null);
  const [signerName, setSignerName] = useState('');
  // True when sigData came from the signature saved at registration (and is
  // already the active row in `signatures`) rather than a fresh drawing in
  // the modal — lets handleOhereza skip re-saving/duplicating it.
  const [sigIsSaved, setSigIsSaved] = useState(false);
  const [showSigModal, setShowSigModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [toast, setToast] = useState(null);
  const [sending, setSending] = useState(false);
  const textareaRef = useRef(null);
  // Step 1 = "Amakuru ya Raporo" (title/author/department/recipient/date).
  // Step 2 = writing, signing, and saving — reached via "Komeza" once step 1
  // is filled in, so the user never has to scroll past a long form to find
  // the next action.
  const [step, setStep] = useState(1);

  // Resolve final recipient value
  const finalRecipient = recipient === 'Andika' ? recipientCustom.trim() : recipient;

  // Auto-generate filename: raporo_[author]_[recipient]_[date]
  const fileName = [
    'raporo',
    author.trim().replace(/\s+/g, '_') || 'uwakoze',
    finalRecipient.replace(/\s+/g, '_') || 'igenewe',
    date || new Date().toISOString().split('T')[0],
  ].join('_');

  const wordCount = content.trim() ? content.trim().split(/\s+/).length : 0;
  const charCount = content.length;
  const lineCount = content ? content.split('\n').length : 0;
  const canExport = stripHtml(content).length > 0;

  const autoGrow = useCallback(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = Math.max(ta.scrollHeight, 400) + 'px';
  }, []);

  useEffect(() => { autoGrow(); }, [content, autoGrow]);

  // Auto-apply the signature captured at registration, so the leader isn't
  // asked to draw a brand new one for every report.
  useEffect(() => {
    let cancelled = false;
    axiosInstance.get('/signature/my-signature')
      .then((res) => {
        if (cancelled) return;
        if (res.data?.hasSignature && res.data.signature?.signature_image) {
          setSigData(res.data.signature.signature_image);
          setSignerName(res.data.signature.fullname || jwtFullname);
          setSigIsSaved(true);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const notify = (msg, type = 'ok') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  // ── Step 1 → Step 2 ──
  const handleKomeza = () => {
    if (!title.trim() || !finalRecipient) {
      notify('Uzuza umutwe wa raporo n\'uwo raporo igenewe mbere yo gukomeza', 'err');
      return;
    }
    if (userRole === 'Isibo' && finalRecipient === 'Akagari') {
      notify('Ntabwo wemerewe kohereza raporo ku Akagari utanyuze ku Mudugudu', 'err');
      return;
    }
    setStep(2);
  };

  // ── Validation ──
  const validate = () => {
    if (!title.trim() || !stripHtml(content) || !finalRecipient) {
      notify('Uzuza amakuru yose harimo n\'uwo raporo igenewe', 'err');
      return false;
    }
    if (userRole === 'Isibo' && finalRecipient === 'Akagari') {
      notify('Ntabwo wemerewe kohereza raporo ku Akagari utanyuze ku Mudugudu', 'err');
      return false;
    }
    return true;
  };

  const handleSelectTemplate = (t) => {
    setTitle(t.title);
    setDepartment(t.department);
    setContent(t.content);
    setActiveTemplate(t);
    notify(`Inyandikorugero "${t.name}" yashyizwe mu nyandiko ✓`);
  };

  // ── Bika (save locally as PDF/Word) ──
  const handleExport = async (type) => {
    if (!validate()) return;
    const data = {
      title, author, department, recipient: finalRecipient, date, content,
      signature: sigData, signerName,
      signedDate: new Date().toLocaleDateString('fr-RW', { year: 'numeric', month: 'long', day: 'numeric' }),
    };
    try {
      if (type === 'pdf') await exportPDF(data, fileName);
      else exportWord(data, fileName);
      notify(`Byabitswe nka ${type.toUpperCase()} ✓`);
    } catch (e) {
      console.error(e);
      notify('Kubika byanze!', 'err');
    }
  };

  // ── Ohereza (save to DB + sign + send) ──
  const handleOhereza = async () => {
    if (!validate()) return;
    if (!sigData) {
      notify('Nyamuneka shyiraho umukono mbere yo kohereza raporo.', 'err');
      return;
    }
    setSending(true);
    try {
      // 1. Save signature to signatures table — only when it's a fresh
      // drawing from this session; a signature already fetched from
      // registration is already the active row, so re-saving it here would
      // just deactivate it and insert a duplicate.
      if (!sigIsSaved) {
        await axiosInstance.post('/signature/save', {
          signature_image: sigData,
          signature_type: 'Drawn',
          fullname: author,
          title_position: department,
        });
        setSigIsSaved(true);
      }
      // 2. Create the report — include signature_image_snapshot directly
      const statut = recipientToStatut(finalRecipient);
      const createRes = await axiosInstance.post('/raporo', {
        umutwe_wa_raporo: title,
        ibisobanuro: content,
        itariki_ya_raporo: date,
        yakozwe_na: author,
        umudugudu: department,
        uwo_raporo_igenewe: finalRecipient,
        statut,
        signature_image_snapshot: sigData,
        signed_by_name: author,
        signed_by_role: userRole || 'Isibo',
      });
      const newId = createRes.data?.id;
      if (newId) {
        // 3. Mark as sent
        await axiosInstance.post(`/raporo/${newId}/send`);
      }
      notify(`✅ Raporo yoherejwe kuri ${finalRecipient}!`);
      // sigData is intentionally kept — it's the leader's standing signature,
      // not a per-report entry, so it should stay applied for the next one.
      setTitle(''); setContent(''); setAuthor(jwtFullname); setDepartment('');
    } catch (err) {
      console.error(err);
      notify(err.response?.data?.error || 'Kohereza byanze. Gerageza nanone.', 'err');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen p-4 md:p-6" style={{ background: 'linear-gradient(135deg, #f0f4ff 0%, #fafafa 60%, #f0fdf4 100%)' }}>
      {/* Toast */}
      {toast && (
        <div className={`fixed top-5 right-5 z-50 flex items-center gap-2 px-4 py-3 rounded-2xl shadow-xl text-sm font-semibold animate-slide-in
          ${toast.type === 'ok' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}>
          {toast.type === 'ok' ? '✅' : '⚠️'} {toast.msg}
        </div>
      )}

      {/* Template Modal */}
      {showTemplateModal && (
        <TemplateModal
          onClose={() => setShowTemplateModal(false)}
          onSelect={handleSelectTemplate}
        />
      )}

      {/* Signature Modal */}
      {showSigModal && (
        <SignatureModal
          onClose={() => setShowSigModal(false)}
          onSave={(data) => { setSigData(data); setSignerName(author); setSigIsSaved(false); setShowSigModal(false); notify('Umukono wabitswe ✓'); }}
        />
      )}

      {/* Page Header — KwandikaRaporoCard (full width, no sibling crushing it) */}
      <div className="max-w-6xl mx-auto mb-7">
        <KwandikaRaporoCard
          onClick={() => setShowTemplateModal(true)}
          onTemplateClick={() => setShowTemplateModal(true)}
        />
      </div>

      {/* 2-Column Layout */}
      <div className="max-w-6xl mx-auto flex flex-col lg:flex-row gap-6">

        {/* ── LEFT: Editor (70%) ── */}
        <div className="flex-1 lg:w-[70%] flex flex-col gap-5">

          {/* Meta fields — step 1 only; hidden entirely once step 2 opens
              instead of just scrolled past, so nothing to scroll through. */}
          {step === 1 && (
          <div className="bg-white rounded-2xl shadow-md border border-blue-50 p-6">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-blue-600 inline-block"></span>
                <span className="text-sm font-bold text-gray-700 uppercase tracking-wider">Amakuru ya Raporo</span>
              </div>
              {activeTemplate && (
                <div className="flex items-center gap-1.5 px-3 py-1 bg-blue-50 border border-blue-200 rounded-xl">
                  <span className="text-sm">{activeTemplate.icon}</span>
                  <span className="text-xs font-bold text-blue-700">{activeTemplate.name}</span>
                  <button onClick={() => { setActiveTemplate(null); setContent(''); setTitle(''); setDepartment(''); }}
                    className="text-blue-400 hover:text-red-500 ml-1 text-xs font-bold transition-colors">✕</button>
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="block text-xs font-bold text-blue-600 mb-1.5 uppercase tracking-widest">Umutwe wa Raporo</label>
                <input value={title} onChange={e => setTitle(e.target.value)}
                  placeholder="Injiza umutwe wa raporo..."
                  className="w-full px-4 py-3 border-2 border-gray-100 rounded-xl text-sm text-gray-800 bg-gray-50 focus:outline-none focus:border-blue-500 focus:bg-white transition-all shadow-sm placeholder-gray-300 font-medium" />
              </div>
              <div>
                <label className="block text-xs font-bold text-blue-600 mb-1.5 uppercase tracking-widest">Uwakoze Raporo</label>
                <input value={author} onChange={e => setAuthor(e.target.value)}
                  placeholder="Izina ryawe..."
                  className="w-full px-4 py-3 border-2 border-gray-100 rounded-xl text-sm text-gray-800 bg-gray-50 focus:outline-none focus:border-blue-500 focus:bg-white transition-all shadow-sm placeholder-gray-300" />
              </div>
              <div>
                <label className="block text-xs font-bold text-blue-600 mb-1.5 uppercase tracking-widest">Ishami</label>
                <input value={department} onChange={e => setDepartment(e.target.value)}
                  placeholder="Ishami cyangwa serivisi..."
                  className="w-full px-4 py-3 border-2 border-gray-100 rounded-xl text-sm text-gray-800 bg-gray-50 focus:outline-none focus:border-blue-500 focus:bg-white transition-all shadow-sm placeholder-gray-300" />
              </div>

              {/* ── Uwo Raporo Igenewe ── */}
              <div>
                <label className="block text-xs font-bold text-blue-600 mb-1.5 uppercase tracking-widest">
                  Uwo Raporo Igenewe <span className="text-red-500">*</span>
                </label>
                <select
                  value={recipient}
                  onChange={e => { setRecipient(e.target.value); setRecipientCustom(''); }}
                  className="w-full px-4 py-3 border-2 border-gray-100 rounded-xl text-sm text-gray-800 bg-gray-50 focus:outline-none focus:border-blue-500 focus:bg-white transition-all shadow-sm cursor-pointer"
                >
                  <option value="">Hitamo uwo igenewe...</option>
                  <option value="Umudugudu">Umudugudu</option>
                  {userRole !== 'Isibo' && <option value="Akagari">Akagari</option>}
                  <option value="Andika">Andika...</option>
                </select>
                {recipient === 'Andika' && (
                  <input
                    value={recipientCustom}
                    onChange={e => setRecipientCustom(e.target.value)}
                    placeholder="Shyiramo izina ry'uwo igenewe"
                    className="mt-2 w-full px-4 py-3 border-2 border-blue-200 rounded-xl text-sm text-gray-800 bg-white focus:outline-none focus:border-blue-500 transition-all shadow-sm"
                  />
                )}
                {/* Show resolved recipient as badge */}
                {finalRecipient && (
                  <div className="mt-1.5 flex items-center gap-1.5">
                    <span className="text-xs text-gray-400">Uwo bireba:</span>
                    <span className="text-xs font-bold text-blue-700 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded-full">{finalRecipient}</span>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-xs font-bold text-blue-600 mb-1.5 uppercase tracking-widest">Itariki</label>
                <input type="date" value={date} onChange={e => setDate(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-100 rounded-xl text-sm text-gray-800 bg-gray-50 focus:outline-none focus:border-blue-500 focus:bg-white transition-all shadow-sm" />
              </div>
            </div>

            {/* Step 1 → Step 2: skips straight to writing/signing/saving —
                no need to scroll past this card to find what's next. */}
            <div className="flex justify-end mt-5 pt-5 border-t border-gray-100">
              <button
                onClick={handleKomeza}
                className="flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold text-white transition-all hover:-translate-y-0.5 hover:shadow-lg active:translate-y-0"
                style={{ background: 'linear-gradient(135deg, #1d4ed8, #2563eb)' }}
              >
                Komeza <span aria-hidden="true">→</span>
              </button>
            </div>
          </div>
          )}

          {step === 2 && (
          <>
          {/* Back to step 1 */}
          <button
            onClick={() => setStep(1)}
            className="flex items-center gap-1.5 text-xs font-bold text-blue-600 hover:text-blue-800 transition-colors w-fit"
          >
            <span aria-hidden="true">←</span> Subira ku makuru ya raporo
          </button>

          {/* Textarea */}
          <div className="bg-white rounded-2xl shadow-md border border-blue-50 p-6 flex flex-col gap-3">
            <div className="flex items-center gap-2 mb-1">
              <span className="w-1 h-5 rounded-full bg-green-500 inline-block"></span>
              <span className="text-sm font-bold text-gray-700 uppercase tracking-wider">Inyandiko</span>
            </div>
            <textarea
              ref={textareaRef}
              value={content}
              onChange={e => setContent(e.target.value)}
              placeholder="Andika raporo yawe hano..."
              className="w-full min-h-[400px] resize-none border-none outline-none text-sm text-gray-800 leading-loose bg-transparent placeholder-gray-300"
              style={{ fontFamily: 'Georgia, serif', lineHeight: '2', backgroundImage: 'repeating-linear-gradient(transparent, transparent 31px, #e5e7eb 31px, #e5e7eb 32px)', backgroundAttachment: 'local' }}
            />
            {/* Stats row */}
            <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-100">
              <span className="bg-gray-100 px-2 py-1 rounded-lg text-xs font-semibold text-gray-400">{wordCount} amagambo</span>
              <span className="text-gray-300 text-xs">·</span>
              <span className="bg-gray-100 px-2 py-1 rounded-lg text-xs font-semibold text-gray-400">{lineCount} imirongo</span>
            </div>

            {/* Ongeraho Umukono — redesigned card */}
            <div style={{ marginTop: 4 }}>
              <OngerahoUmukonoCard sigData={sigData} onClick={() => setShowSigModal(true)} />
            </div>
          </div>

          {/* Filename — redesigned IzinaRyaDosiyeCard */}
          <IzinaRyaDosiyeCard fileName={fileName} />

          {/* Spacer so the fixed action bar below never covers the last
              card, however far the user scrolls. */}
          <div className="h-20" />

          {/* ── Action Buttons: Bika + Ohereza ──
              Fixed to the viewport (not sticky) so they're visible the
              instant step 2 opens — zero scrolling needed to reach them,
              not just once the user has already started scrolling.
              Floats centered as its own pill instead of spanning the
              two-column layout, so it doesn't need to know the host
              sidebar's width. Offset above bottom-24 on small screens so
              it clears the host layout's fixed mobile bottom-nav bar
              (hidden again at lg:); z-50 keeps it on top of that bar in
              layouts where it isn't in an isolated stacking context.
              Stays BELOW TemplateModal/SignatureModal (z-[60]) on purpose —
              this bar must never cover a modal's own controls, e.g. the
              signature pad's save button. */}
          <div className="fixed bottom-24 lg:bottom-5 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-lg px-5 py-3.5 flex gap-2.5 flex-wrap justify-center items-center bg-white/95 backdrop-blur-sm border border-gray-100 shadow-[0_8px_28px_rgba(15,23,42,0.16)] rounded-2xl">
            <button
              onClick={() => handleExport('pdf')}
              disabled={!canExport}
              className="flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-bold text-white disabled:opacity-40 disabled:cursor-not-allowed transition-all hover:-translate-y-0.5 hover:shadow-lg active:translate-y-0"
              style={{ background: 'linear-gradient(135deg, #1d4ed8, #2563eb)' }}
            >
              💾 Bika (PDF)
            </button>
            <button
              onClick={() => handleExport('doc')}
              disabled={!canExport}
              className="flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-bold text-blue-700 bg-blue-50 border-2 border-blue-200 hover:bg-blue-100 disabled:opacity-40 disabled:cursor-not-allowed transition-all hover:-translate-y-0.5 active:translate-y-0"
            >
              📘 Bika (Word)
            </button>
            <button
              onClick={handleOhereza}
              disabled={sending || !canExport || !sigData}
              title={!sigData ? 'Nyamuneka shyiraho umukono mbere yo kohereza raporo.' : ''}
              className="flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-bold text-white disabled:opacity-40 disabled:cursor-not-allowed transition-all hover:-translate-y-0.5 hover:shadow-lg active:translate-y-0"
              style={{ background: sending ? '#9ca3af' : 'linear-gradient(135deg, #059669, #10b981)' }}
            >
              {sending ? '⏳ Kohereza...' : `📤 Ohereza${finalRecipient ? ` → ${finalRecipient}` : ''}${!sigData ? ' (Shyiraho Umukono)' : ''}`}
            </button>
          </div>
          </>
          )}
        </div>

        {/* ── RIGHT: Tools (30%) ── */}
        <div className="lg:w-[30%] flex flex-col gap-5">
          <SummaryPanel
            title={title}
            author={author}
            recipient={finalRecipient}
            wordCount={wordCount}
            charCount={charCount}
            lineCount={lineCount}
          />
        </div>
      </div>

      <style>{`
        @keyframes slide-in { from { transform: translateX(120%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        .animate-slide-in { animation: slide-in 0.3s cubic-bezier(.22,1,.36,1); }
        @keyframes sig-pulse { 0%,100% { box-shadow: 0 0 0 0 rgba(37,99,235,0.15); } 50% { box-shadow: 0 0 0 6px rgba(37,99,235,0); } }
        .sig-pulse { animation: sig-pulse 2s infinite; }
      `}</style>
    </div>
  );
};

export default ReportPage;
